import React from 'react';
import { normalizeAppHostedUploadUrl } from '../../utils/normalizeAppHostedUploadUrl';

export interface ReleaseHeroSectionProps {
  title?: string | null;
  summary?: string | null;
  heroImageUrl?: string | null;
  compact?: boolean;
  /** When set with a hero image, title/summary begin at this vertical percent (e.g. 70). */
  textFromPercent?: number;
  /** Place text N lines above the bottom fade edge (overrides textFromPercent). */
  textLinesBeforeFade?: number;
  /** Tailwind blur class for the hero image (default blur-[3px]). */
  imageBlurClass?: string;
  /** Height of bottom fade overlay as percent of card (default 78). */
  fadeHeightPercent?: number;
  minHeightPx?: number;
  /** Fixed card height when set (e.g. percent of parent modal). */
  heightPx?: number;
  titleClassName?: string;
  summaryClassName?: string;
  /** When true, title and summary scroll together inside the hero card instead of clipping. */
  scrollableSummary?: boolean;
  /** Fixed scroll panel height class when scrollableSummary is used (overrides textFromPercent). */
  scrollPanelHeightClass?: string;
  className?: string;
  rtl?: boolean;
}

export const ReleaseHeroSection: React.FC<ReleaseHeroSectionProps> = ({
  title,
  summary,
  heroImageUrl,
  compact = false,
  textFromPercent,
  textLinesBeforeFade,
  imageBlurClass = 'blur-[3px]',
  fadeHeightPercent = 78,
  minHeightPx,
  heightPx,
  titleClassName,
  summaryClassName,
  scrollableSummary = false,
  scrollPanelHeightClass = 'h-[4.25rem] sm:h-[5rem]',
  className = '',
  rtl = false,
}) => {
  const imageUrl = heroImageUrl?.trim() ? normalizeAppHostedUploadUrl(heroImageUrl) : '';
  const hasText = Boolean(title || summary);
  if (!hasText && !imageUrl) return null;

  const titleClass = titleClassName ?? (compact ? 'text-xs font-semibold' : 'text-sm font-semibold');
  const summaryClass =
    summaryClassName ?? (compact ? 'text-xs leading-relaxed' : 'text-sm leading-relaxed');
  const fadeStartPercent = 100 - fadeHeightPercent;
  const textTopStyle: React.CSSProperties | undefined =
    imageUrl && textLinesBeforeFade != null
      ? {
          top: `max(0.5rem, calc(${fadeStartPercent}% - ${textLinesBeforeFade * 1.25}em))`,
        }
      : undefined;
  const textTopPercent =
    imageUrl && textTopStyle == null && textFromPercent != null
      ? Math.min(Math.max(textFromPercent, 0), 90)
      : null;

  const textPositionStyle: React.CSSProperties | undefined =
    textTopStyle ?? (textTopPercent != null ? { top: `${textTopPercent}%` } : undefined);

  const summaryBody = summary ? (
    <p className={`${summaryClass} text-slate-600 dark:text-slate-300`}>{summary}</p>
  ) : null;

  const textBlock = (
    <>
      {title ? (
        <p className={`${titleClass} text-slate-900 dark:text-slate-100`}>{title}</p>
      ) : null}
      {summaryBody ? <div className={title ? 'mt-1' : ''}>{summaryBody}</div> : null}
    </>
  );

  if (imageUrl) {
    const sizeStyle: React.CSSProperties | undefined =
      heightPx != null
        ? { height: `${heightPx}px`, minHeight: `${heightPx}px` }
        : minHeightPx != null
          ? { minHeight: `${minHeightPx}px` }
          : undefined;

    return (
      <div
        className={`relative min-h-[12rem] overflow-hidden rounded-xl border border-amber-200/80 dark:border-amber-800/50 ${className}`}
        dir={rtl ? 'rtl' : undefined}
        style={sizeStyle}
      >
        <img
          src={imageUrl}
          alt=""
          className={`pointer-events-none absolute inset-0 h-full w-full scale-[1.04] object-cover ${imageBlurClass}`}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/10 via-black/5 to-transparent dark:from-black/25 dark:via-black/10"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-white via-white/92 to-transparent dark:from-gray-900 dark:via-gray-900/92 dark:to-transparent"
          style={{ height: `${fadeHeightPercent}%` }}
          aria-hidden
        />
        {hasText ? (
          <div
            className={`absolute inset-x-0 z-10 ${rtl ? 'font-persian' : ''} ${
              scrollableSummary && textPositionStyle != null
                ? `bottom-0 flex flex-col ${scrollPanelHeightClass}`
                : `px-4 pb-3 ${textTopPercent != null ? 'pt-1' : textTopStyle != null ? '' : 'bottom-0 top-0 flex flex-col justify-end pt-1'}`
            }`}
            style={
              scrollableSummary && textPositionStyle != null ? undefined : textPositionStyle
            }
          >
            {scrollableSummary && textPositionStyle != null ? (
              <div className="relative min-h-0 flex-1">
                <div className="release-hero-text-panel pointer-events-none absolute inset-0" aria-hidden />
                <div className="release-hero-text-scroll relative z-10 h-full min-h-0 overflow-y-auto overscroll-y-contain px-3 py-2.5 [-webkit-overflow-scrolling:touch]">
                  {textBlock}
                </div>
              </div>
            ) : (
              textBlock
            )}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border border-amber-200/80 bg-white/60 px-4 py-3 dark:border-amber-800/50 dark:bg-gray-900/40 ${className}`}
      dir={rtl ? 'rtl' : undefined}
    >
      {scrollableSummary ? (
        <div className="max-h-32 overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]">
          {textBlock}
        </div>
      ) : (
        textBlock
      )}
    </div>
  );
};

export default ReleaseHeroSection;
